import { useEffect, useState } from 'react';
import { Heading } from '@acb_devel/design-system';
import { Text } from '@acb_devel/design-system';
import { DataTable } from '@components/assessors/molecules/DataTable/DataTable';
import { DataTablePaginator } from '@components/assessors/molecules/DataTablePaginator/DataTablePaginator';
import { EventAuditAction, getEventsAudits } from '@services/eventsAudits';
import type { EventAudit } from '@services/eventsAudits';
import { printEventDate } from '@utils/date';
import { useEventsContext } from '@contexts/EventsContext';

import styles from './EventsLogsTable.module.scss';

export function EventsLogsTable() {
  const { events } = useEventsContext();

  const [eventsLogs, setEventsLogs] = useState<EventAudit[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [sortKey, setSortKey] = useState('');
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    setLoading(true);
    getEventsAudits({
      offset: (page - 1) * pageSize,
      limit: pageSize,
      ordering: sortKey
        ? `${direction === 'asc' ? '' : '-'}${sortKey}`
        : undefined,
    }).then(({ count, results }) => {
      setTotalRows(count);
      setEventsLogs(results);
      setLoading(false);
    });
  }, [page, pageSize, sortKey, direction, events]);

  return (
    <div className={styles.eventsLogsTable}>
      <div className={styles.eventsLogsTable__header}>
        <Heading as="h2" variant="heading-1">
          Historial de cambios
        </Heading>
      </div>
      <div className={styles.eventsLogsTable__content}>
        <DataTable
          data={eventsLogs}
          sortKey={sortKey}
          loading={loading}
          direction={direction}
          onSort={(sortKey, direction) => {
            setSortKey(sortKey);
            setDirection(direction);
          }}
          columns={[
            {
              sortKey: 'created_at',
              label: 'Fecha acción',
              render: (row) => (
                <Text variant="body-2">
                  {new Date(row.createdAt).toLocaleDateString()} ·{' '}
                  {new Date(row.createdAt)
                    .getHours()
                    .toString()
                    .padStart(2, '0')}
                  :
                  {new Date(row.createdAt)
                    .getMinutes()
                    .toString()
                    .padStart(2, '0')}
                </Text>
              ),
            },
            {
              sortKey: 'action',
              label: 'Acción',
              render: (row) => {
                switch (row.action) {
                  case EventAuditAction.CREATE:
                    return (
                      <Heading as="p" variant="subhead">
                        Creación
                      </Heading>
                    );
                  case EventAuditAction.EDIT:
                    return (
                      <Heading as="p" variant="subhead">
                        Edición
                      </Heading>
                    );
                  case EventAuditAction.DELETE:
                    return (
                      <Heading
                        as="p"
                        variant="subhead"
                        className={styles.eventsLogsTable__actionDelete}
                      >
                        Borrado
                      </Heading>
                    );
                  default:
                    return (
                      <Heading as="p" variant="subhead">
                        -
                      </Heading>
                    );
                }
              },
            },
            {
              sortKey: 'event_name',
              label: 'Nombre evento',
              render: (row) => (
                <Text variant="body-2">{row.event.fullName}</Text>
              ),
            },
            {
              sortKey: 'start_event',
              label: 'Fecha evento',
              render: (row) => (
                <Text variant="body-2">
                  {printEventDate(row.event.startEvent, row.event.endEvent)}
                </Text>
              ),
            },
            {
              sortKey: 'email_address',
              label: 'Usuario',
              render: (row) => (
                <Text variant="body-2">{row.user.emailAddress}</Text>
              ),
            },
          ]}
        />
        <DataTablePaginator
          totalRows={totalRows}
          perPage={pageSize}
          page={page}
          setPage={setPage}
          className={styles.paginator}
        />
      </div>
    </div>
  );
}
